import {
    FAVOURITES_IGNORED_ERROR_CODES,
    removeDesigns,
    removeItems,
} from 'api/list';
import { logRemoveItemFromFavourites } from 'utils/analytics';
import {
    MultipleItemPayload,
    publish,
    REMOVE_FAILURE,
    REMOVE_LOADING,
    REMOVE_SUCCESS,
    SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_FAIL,
    SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_SUCCESS,
    SingleItemPayload,
} from 'utils/pubsub';
import { reportError } from 'utils/sentry';
import { favouritesUrl } from 'utils/settings';
import { refreshCache } from 'utils/store';
import translate from 'utils/translate';
import { addToSavedForLaterList } from '../addToList/addToSavedForLaterList';

type RemoveItemFromListParams = {
    payload: SingleItemPayload;
    quantity: number;
    savedForLaterListId?: string;
};
type RemoveItemsFromListParams = {
    payload: MultipleItemPayload;
    savedForLaterListId: string;
};

export const removeItemFromList = async ({
    payload,
    quantity,
    savedForLaterListId,
}: RemoveItemFromListParams) => {
    const skipToast = false;
    publish(REMOVE_LOADING, payload);
    try {
        let listId = savedForLaterListId ?? '';
        if (savedForLaterListId) {
            if (payload.productNumber) {
                await removeItems(
                    {
                        itemNos: [payload.productNumber],
                        listId: savedForLaterListId,
                    },
                    payload.clientId
                );
            } else if (payload.design) {
                await removeDesigns(
                    {
                        configurationIds: [payload.design],
                        listId: savedForLaterListId,
                    },
                    payload.clientId
                );
            }

            publish(REMOVE_SUCCESS, { ...payload, skipToast });
            if (!payload.skipSavedForLaterToast) {
                window?.ikea?.toast(
                    translate('AGENT_LISTS_TOAST_REMOVED', {
                        productName: payload.productName,
                        listNames: translate('SAVED_FOR_LATER_LIST_NAME'),
                    }),
                    {
                        label: translate('AGENT_TOAST_UNDO'),
                        callbackOrHref: () =>
                            addToSavedForLaterList({
                                payload,
                                listId: savedForLaterListId,
                            }),
                    },
                    null
                );
            }
        } else {
            if (payload.productNumber) {
                const res = await removeItems(
                    {
                        itemNos: [payload.productNumber],
                        listId: null,
                    },
                    payload.clientId
                );

                listId = res.data.list.listId;
            } else if (payload.design) {
                const res = await removeDesigns(
                    {
                        configurationIds: [payload.design],
                        listId: null,
                    },
                    payload.clientId
                );

                listId = res.data.list.listId;
            }

            publish(REMOVE_SUCCESS, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('AGENT_LISTS_TOAST_REMOVED', {
                    productName: payload.productName,
                    listNames: translate('DEFAULT_LIST_NAME'),
                }),
                {
                    label: translate('AGENT_TOAST_SHOW'),
                    callbackOrHref: favouritesUrl,
                },
                null
            );
        }
        refreshCache();

        logRemoveItemFromFavourites({
            quantity,
            listId,
            source: payload.source,
            ga: payload?.ga,
            productNumber: payload.productNumber ?? payload.design ?? '',
            productType:
                payload.productType ?? (payload.design ? 'planner' : ''),
        });
    } catch (e) {
        const ignoreError = !!FAVOURITES_IGNORED_ERROR_CODES.find(errorCode =>
            (e as Error).message?.includes(errorCode)
        );
        // If the error is one of the whitelisted ones, do not report it and simulate that the item was removed from the list successfully.
        // We can have this behaviour because the whitelisted errors occur due to sync issues between different browser tabs.
        // Sync issues caused by authentication sync can lead to list operation failures
        if (ignoreError) {
            refreshCache();
            publish(REMOVE_SUCCESS, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('AGENT_LISTS_TOAST_REMOVED_GENERIC', {
                    productName: payload.productName,
                }),
                {
                    label: translate('AGENT_TOAST_SHOW'),
                    callbackOrHref: favouritesUrl,
                },
                null
            );
            return true;
        }
        reportError(e as Error);
        publish(REMOVE_FAILURE, { ...payload, skipToast });
        window?.ikea?.toast(
            translate('GENERIC_ERROR_TOAST'),
            {
                label: translate('AGENT_TOAST_SHOW'),
                callbackOrHref: favouritesUrl,
            },
            null
        );
        return false;
    }
};
export const removeItemsFromSavedForLaterList = async ({
    payload,
    savedForLaterListId,
}: RemoveItemsFromListParams) => {
    const skipToast = false;
    try {
        const itemNos = payload.items?.map(item => item.productNumber);
        const configurationIds =
            payload.designs?.map(design => design.configurationId) ?? '';
        if (payload.items) {
            await removeItems(
                {
                    itemNos,
                    listId: savedForLaterListId,
                },
                payload.clientId
            );
            payload.items.forEach(item => {
                logRemoveItemFromFavourites({
                    quantity: Number(item.quantity),
                    listId: savedForLaterListId,
                    source: payload.source,
                    ga: payload?.ga,
                    productNumber: item.productNumber,
                    productType: item.productType,
                });
            });
        }
        if (payload.designs) {
            await removeDesigns(
                {
                    configurationIds,
                    listId: savedForLaterListId,
                },
                payload.clientId
            );
            payload.designs.forEach(design => {
                logRemoveItemFromFavourites({
                    quantity: Number(design.quantity),
                    listId: savedForLaterListId,
                    source: payload.source,
                    ga: payload?.ga,
                    productNumber: design.configurationId,
                    productType: 'planner',
                });
            });
        }

        publish(SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_SUCCESS, {
            ...payload,
            skipToast,
        });
        refreshCache();
    } catch (e) {
        reportError(e as Error);
        publish(SAVE_FOR_LATER_REMOVE_ITEMS_REQUEST_FAIL, {
            ...payload,
            skipToast,
        });
        window?.ikea?.toast(
            translate('GENERIC_ERROR_TOAST'),
            {
                label: translate('AGENT_TOAST_SHOW'),
                callbackOrHref: favouritesUrl,
            },
            null
        );
        return false;
    }
};
